import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { ShieldAlert } from "lucide-react";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type SupportTicketSummary = {
  id: string;
  ticketNo: string;
  subject: string;
  status: "open" | "closed" | "archived";
  createdAt: number;
  updatedAt: number;
  userId: string;
  userEmail: string;
  userFullName?: string;
  messagesCount: number;
  lastMessage: string;
  lastActor: "user" | "admin";
};

type SupportMessage = {
  id: string;
  createdAt: number;
  actor: "user" | "admin";
  message: string;
  senderName: string;
};

type SupportTicketDetail = SupportTicketSummary & {
  messages: SupportMessage[];
};

async function fetchTickets() {
  const res = await adminFetch("/api/admin/numeroloji/support/tickets");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ticket listesi alınamadı.");
  }
  return (Array.isArray(json.items) ? json.items : []) as SupportTicketSummary[];
}

async function fetchTicketDetail(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/support/tickets/${encodeURIComponent(id)}`);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ticket detayı alınamadı.");
  }
  return json.item as SupportTicketDetail;
}

async function replyTicket(input: { id: string; message: string }) {
  const res = await adminFetch(`/api/admin/numeroloji/support/tickets/${encodeURIComponent(input.id)}/reply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: input.message }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yanıt gönderilemedi.");
  }
  return json.item as SupportTicketDetail | null;
}

async function updateStatus(input: { id: string; status: "open" | "closed" | "archived" }) {
  const res = await adminFetch(`/api/admin/numeroloji/support/tickets/${encodeURIComponent(input.id)}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: input.status }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Durum güncellenemedi.");
  }
  return json.item as SupportTicketDetail | null;
}

function formatDate(ts: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(ts * 1000));
  } catch {
    return String(ts);
  }
}

export default function AdminNumerolojiSupport() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const [selectedId, setSelectedId] = useState("");
  const [reply, setReply] = useState("");
  const [listMode, setListMode] = useState<"active" | "archive">("active");

  const listQuery = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: fetchTickets,
    enabled: configured && authenticated,
    staleTime: 5_000,
  });

  const visibleTickets = useMemo(
    () =>
      (listQuery.data ?? []).filter((ticket) =>
        listMode === "archive" ? ticket.status === "archived" : ticket.status !== "archived",
      ),
    [listQuery.data, listMode],
  );

  useEffect(() => {
    if (!visibleTickets.length) {
      setSelectedId("");
      return;
    }
    if (!selectedId || !visibleTickets.some((item) => item.id === selectedId)) {
      setSelectedId(visibleTickets[0].id);
    }
  }, [visibleTickets, selectedId]);

  const detailQuery = useQuery({
    queryKey: ["admin-support-ticket", selectedId],
    queryFn: () => fetchTicketDetail(selectedId),
    enabled: configured && authenticated && Boolean(selectedId),
    staleTime: 3_000,
  });

  const replyMutation = useMutation({
    mutationFn: replyTicket,
    onSuccess: async (item) => {
      setReply("");
      await queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      if (item?.id) {
        await queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", item.id] });
      }
    },
  });

  const statusMutation = useMutation({
    mutationFn: updateStatus,
    onSuccess: async (item) => {
      await queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      if (item?.id) {
        await queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", item.id] });
      }
    },
  });

  const selectedTicket = useMemo(() => detailQuery.data ?? null, [detailQuery.data]);

  if (!configured) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-500" />
            <div>
              <div className="text-base font-semibold">Admin girişi ayarlı değil</div>
              <div className="mt-1 text-sm text-muted-foreground">Sunucu tarafında admin girişi ayarlanmalı.</div>
              <div className="mt-4">
                <Button asChild className="rounded-full">
                  <Link to="/admin/numeroloji">Admin panel</Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <main className="mx-auto w-full max-w-[1500px] flex-1 p-4 md:p-6">
          <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
            <Card className="p-3">
              <h1 className="px-2 text-xl font-bold">Destek Ticketları</h1>
              <div className="mt-3 mb-3 flex gap-2 px-1">
                <Button type="button" variant={listMode === "active" ? "default" : "outline"} className="h-8 rounded-full px-3 text-xs" onClick={() => setListMode("active")}>
                  Aktif
                </Button>
                <Button type="button" variant={listMode === "archive" ? "default" : "outline"} className="h-8 rounded-full px-3 text-xs" onClick={() => setListMode("archive")}>
                  Arşiv
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {listQuery.isLoading ? <div className="p-2 text-sm text-slate-500">Yükleniyor...</div> : null}
                {listQuery.isError ? <div className="p-2 text-sm text-rose-600">{(listQuery.error as Error).message}</div> : null}
                {!listQuery.isLoading && !visibleTickets.length ? <div className="p-2 text-sm text-slate-500">{listMode === "archive" ? "Arşivde ticket yok." : "Henüz ticket yok."}</div> : null}
                {visibleTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => setSelectedId(ticket.id)}
                    className={cn(
                      "w-full rounded-xl border p-3 text-left transition-colors",
                      selectedId === ticket.id ? "border-blue-400 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-900",
                    )}
                  >
                    <div className="line-clamp-1 text-sm font-semibold">#{ticket.ticketNo} • {ticket.subject}</div>
                    {ticket.userFullName ? <div className="mt-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">{ticket.userFullName}</div> : null}
                    <div className="mt-0.5 text-xs text-slate-500">{ticket.userEmail}</div>
                    <div className="mt-1 text-xs text-slate-500">{ticket.status === "archived" ? "Arşivde" : ticket.status === "closed" ? "Kapalı" : "Açık"} • {formatDate(ticket.updatedAt)}</div>
                  </button>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              {!selectedId ? (
                <div className="text-sm text-slate-500">Soldan bir ticket seç.</div>
              ) : detailQuery.isLoading ? (
                <div className="text-sm text-slate-500">Mesajlar yükleniyor...</div>
              ) : detailQuery.isError ? (
                <div className="text-sm text-rose-600">{(detailQuery.error as Error).message}</div>
              ) : selectedTicket ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                    <div>
                      <h2 className="text-lg font-bold">#{selectedTicket.ticketNo} • {selectedTicket.subject}</h2>
                      {selectedTicket.userFullName ? (
                        <div className="text-xs font-medium text-slate-700 dark:text-slate-300">{selectedTicket.userFullName}</div>
                      ) : null}
                      <div className="text-xs text-slate-500">{selectedTicket.userEmail}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", selectedTicket.status === "archived" ? "bg-violet-100 text-violet-700" : selectedTicket.status === "closed" ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700")}>
                        {selectedTicket.status === "archived" ? "Arşivde" : selectedTicket.status === "closed" ? "Kapalı" : "Açık"}
                      </span>
                      {selectedTicket.status !== "archived" ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 rounded-full px-3 text-xs"
                          disabled={statusMutation.isPending}
                          onClick={() => statusMutation.mutate({ id: selectedTicket.id, status: selectedTicket.status === "closed" ? "open" : "closed" })}
                        >
                          {selectedTicket.status === "closed" ? "Yeniden Aç" : "Ticketi Kapat"}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        className="h-8 rounded-full px-3 text-xs"
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate({ id: selectedTicket.id, status: selectedTicket.status === "archived" ? "open" : "archived" })}
                      >
                        {selectedTicket.status === "archived" ? "Arşivden Çıkar" : "Arşive Al"}
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {selectedTicket.messages.map((item) => (
                      <div key={item.id} className={cn("max-w-[90%] rounded-xl p-3 text-sm", item.actor === "admin" ? "ml-auto border border-blue-200 bg-blue-50" : "border border-slate-200 bg-slate-50")}>
                        <div className="mb-1 text-xs font-semibold text-slate-500">{item.senderName} • {formatDate(item.createdAt)}</div>
                        <p className="whitespace-pre-wrap">{item.message}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 border-t pt-4">
                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Kullanıcıya yanıt yaz" className="min-h-[100px]" />
                    <div className="mt-3 flex justify-end">
                      <Button disabled={replyMutation.isPending || selectedTicket.status !== "open"} onClick={() => replyMutation.mutate({ id: selectedTicket.id, message: reply.trim() })}>
                        {replyMutation.isPending ? "Gönderiliyor..." : selectedTicket.status === "archived" ? "Ticket Arşivde" : selectedTicket.status === "closed" ? "Ticket Kapalı" : "Yanıt Gönder"}
                      </Button>
                    </div>
                    {replyMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(replyMutation.error as Error).message}</p> : null}
                    {statusMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(statusMutation.error as Error).message}</p> : null}
                  </div>
                </div>
              ) : null}
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
